<template>
    <LoadingCard v-if="loading" />
    <div v-else>
        <header class="content__title pl-0">
            <h1>Weed Spray diary</h1>

            <div class="actions">
                <b-button :to="{name: 'weed-spray-diary.create-bulk'}"
                          variant="success btn--icon"
                          v-b-tooltip="'Update Weed Spray Diary'">
                    <i class="zmdi zmdi-plus"></i>
                </b-button>
            </div>
        </header>

        <div class="card">
            <div class="bg-primary align-items-center d-flex">
                <h5 class="card-header text-white">All Entries</h5>
                <b-pagination align="right" class="my-auto ml-auto mr-2" :total-rows="pagination.total" v-model="pagination.page"
                              :per-page="pagination.limit" />
            </div>
            <VueDatePicker
                    id="date"
                    :config="dateRangeConfig"
                    v-model="dateRange"
                    class="form-control form-control-lg text-center bg-light"
                    placeholder="Click to filter entries by spray date"
                    name="date" />

            <b-table stacked="md" :items="tableData" :fields="sprayLogsTable.fields" sort-by="sprayed_at" :sort-desc="true"
                     :per-page="pagination.limit" :current-page="pagination.page"
                     :show-empty="true">

                <template slot="block" slot-scope="row">
                    <span class="text-nowrap">{{ row.item.block.name }}</span>
                </template>

                <template slot="applicator" slot-scope="row">
                    {{ row.item.growsafe_number }}
                </template>

                <template slot="sprayed_at" slot-scope="row">
                    <span class="text-nowrap">{{ row.item.sprayed_at | datetime_format }}</span>
                </template>

                <template slot="chemicals" slot-scope="row">
                    <span v-for="entry in [...row.item.entries].sort((a,b) => a.chemical.name.localeCompare(b.chemical.name))"
                          class="badge badge-light mr-1 mb-1">
                        {{ entry.chemical.name }}
                    </span>
                </template>

                <template slot="fertilisers" slot-scope="row">
                    <template v-for="entry in [...row.item.fertiliserEntries].sort((a,b) => a.fertiliser.name.localeCompare(b.fertiliser.name))">
                        <span class="badge badge-light mr-1 mb-1">{{ entry.fertiliser.name }}</span>
                    </template>
                </template>

                <template slot="expand" slot-scope="row">
                    <b-button variant="secondary" size="sm" @click.stop="row.toggleDetails">
                        {{ row.detailsShowing ? 'Hide' : 'Show'}} Details
                    </b-button>
                </template>

                <template slot="row-details" slot-scope="row">
                    <b-button variant="outline-secondary" class="btn-block mb-3" size="sm" @click="attemptDelete(row.item)">
                        <i class="zmdi zmdi-delete"></i> Delete this Diary Entry
                    </b-button>

                    <dl class="row">
                        <dt class="col-2 col-md-3">Time Taken:</dt>
                        <dd class="col-2 col-md-3">
                            <template v-if="row.item.time_taken">
                                {{ row.item.time_taken | minute_format }}
                            </template>
                            <span class="text-muted" v-else>(unknown)</span>
                        </dd>

                        <dt class="col-2 col-md-3">Wind:</dt>
                        <dd class="col-2 col-md-3">
                            <template v-if="row.item.wind_direction && row.item.wind_speed">
                                Speed: {{ windSpeedValues.find(speed => speed.name === row.item.wind_speed).description }},
                                Direction: {{ windDirectionValues.find(direction => direction.name === row.item.wind_direction).description }}
                            </template>
                            <template v-else-if="row.item.wind_direction">
                                Direction: {{ windDirectionValues.find(direction => direction.name === row.item.wind_direction).description }}
                            </template>
                            <template v-else-if="row.item.wind_speed">
                                Speed: {{ windSpeedValues.find(speed => speed.name === row.item.wind_speed).description }}
                            </template>
                            <span class="text-muted" v-else>(unknown)</span>
                        </dd>

                        <dt class="col-2 col-md-3">Cloud Cover:</dt>
                        <dd class="col-2 col-md-3">
                            <template v-if="row.item.cloud_cover">
                                {{ row.item.cloud_cover / 100 | percent_format }}
                            </template>
                            <span class="text-muted" v-else>(unknown)</span>
                        </dd>

                        <dt class="col-2 col-md-3">Temperature:</dt>
                        <dd class="col-2 col-md-3">
                            <template v-if="row.item.temperature">
                                {{ row.item.temperature | number_format }}&deg;c
                            </template>
                            <span class="text-muted" v-else>(unknown)</span>
                        </dd>

                        <dt class="col-2 col-md-3">Applicator (Growsafe):</dt>
                        <dd class="col-2 col-md-3">{{ row.item.growsafe_number }}</dd>

                        <dd class="col-12" v-if="row.item.entries.length > 0">
                            <b-table small striped stacked="md" :items="row.item.entries" :fields="entriesTable.fields" :show-empty="true">
                                <template slot="chemical" slot-scope="entry">
                                    {{ entry.item.chemical.name }}
                                </template>
                                <template slot="active_ingredients" slot-scope="entry">
                                    {{ entry.item.chemical.activeIngredients.map(ing => ing.activeIngredient.name).join(', ') }}
                                </template>
                                <template slot="rate" slot-scope="entry">
                                    {{ entry.item.rate | number_format }}
                                </template>
                                <template slot="target" slot-scope="entry">
                                    {{ entry.item.target.name }}
                                </template>
                                <template slot="weight" slot-scope="entry">
                                    {{ entry.item.weight | number_format }}
                                </template>
                            </b-table>
                        </dd>

                        <dd class="col-12 col-md-10" v-if="row.item.fertiliserEntries.length > 0">
                            <b-table small striped stacked="md" :items="row.item.fertiliserEntries" :fields="fertiliserEntriesTable.fields"
                                     :show-empty="true">
                                <template slot="fertiliser" slot-scope="entry">
                                    {{ entry.item.fertiliser.name }}
                                </template>
                                <template slot="rate" slot-scope="entry">
                                    {{ entry.item.rate | number_format }}
                                </template>
                                <template slot="weight" slot-scope="entry">
                                    {{ entry.item.weight | number_format }}
                                </template>
                            </b-table>
                        </dd>

                        <dt class="col-8 col-sm-3 col-md-2">Comments:</dt>
                        <dd class="col-8 col-sm-9 col-md-10">
                            <div v-if="row.item.comments" style="white-space: pre-wrap;">{{ row.item.comments }}</div>
                            <span class="text-muted" v-else>(none)</span>
                        </dd>
                    </dl>
                </template>
            </b-table>
        </div>

    </div>
</template>

<script>
import listWindDirectionValuesQuery from '@gql/enumValues/queryWindDirectionEnumValues.graphql';
import listWindSpeedValuesQuery from '@gql/enumValues/queryWindSpeedEnumValues.graphql';

import exportSprayLogMutation from '@views/WeedSprayDiary/View/mutationExportSprayLog.graphql';
import deleteSprayLogMutation from '@views/WeedSprayDiary/View/mutationDeleteSprayLog.graphql';
import currentUserQuery from '@views/WeedSprayDiary/View/queryCurrentUser.graphql';
import listSprayLogsQuery from '@views/WeedSprayDiary/View/queryListSprayLogs.graphql';

export default {
  name: 'BrowseWeedSprayDiary',
  apollo: {
    currentUser: {
      query: currentUserQuery,
      update: data => data.currentUser,
    },
    sprayLogs: {
      query: listSprayLogsQuery,
      variables() {
        const variables = { input: {} };

        if (this.hasDateRange) {
          variables.input.sprayed_at_gte = this.startDate;
          variables.input.sprayed_at_lte = this.endDate;
        }

        return variables;
      },
      manual: true,
      result({ data, loading }) {
        if (!loading) {
          this.sprayLogs = data.filterWeedSprayLogs.data;
          this.pagination.total = data.filterWeedSprayLogs.meta.total;
        }
      },
    },
    windSpeedValues: {
      query: listWindSpeedValuesQuery,
      update: data => data.__type.enumValues,
    },
    windDirectionValues: {
      query: listWindDirectionValuesQuery,
      update: data => data.__type.enumValues,
    },
  },
  data() {
    return {
      loading: 0,
      currentUser: null,
      dateRange: null,
      sprayLogs: [],
      pagination: {
        page: 1,
        limit: this.$tableDefaultSmallPageSize,
        total: 0,
      },
      windDirectionValues: [],
      windSpeedValues: [],
      dateRangeConfig: {
        mode: 'range',
        altInput: true,
        enableTime: false,
        nextArrow: '<i class="zmdi zmdi-long-arrow-right" />',
        prevArrow: '<i class="zmdi zmdi-long-arrow-left" />',
        altFormat: 'M j, Y',
        dateFormat: 'Y-m-d',
      },
      sprayLogsTable: {
        fields: [
          { key: 'block', label: 'Block' },
          { key: 'applicator', label: 'Applicator' },
          { key: 'sprayed_at', label: 'Spray Date', sortable: true },
          { key: 'chemicals', label: 'Chemicals Used' },
          { key: 'fertilisers', label: 'Fertilisers Used' },
          { key: 'expand', label: ' ' },
        ],
      },
      entriesTable: {
        fields: [
          { key: 'chemical', label: 'Chemical' },
          { key: 'rate', label: 'Rate(mls/gms per 100 lts)' },
          { key: 'weight', label: 'Amount Used(kgs or lts)' },
        ],
      },
      fertiliserEntriesTable: {
        fields: [
          { key: 'fertiliser', label: 'Fertiliser' },
          { key: 'rate', label: 'Rate(mls/gms per 100 lts)' },
          { key: 'weight', label: 'Amount Used(kgs or lts)' },
        ],
      },
    };
  },
  computed: {
    startDate() {
      if (!this.dateRange || !this.dateRange.includes(' to ')) {
        return null;
      }

      return `${this.dateRange.split(' to ')[0]} 00:00:00`;
    },
    endDate() {
      if (!this.dateRange || !this.dateRange.includes(' to ')) {
        return null;
      }

      return `${this.dateRange.split(' to ')[1]} 23:59:59`;
    },
    hasDateRange() {
      return this.startDate && this.endDate;
    },
    tableData() {
      return this.sprayLogs.map(item => ({
        ...item,
        _showDetails: false,
      }));
    },
  },
  methods: {
    attemptExport() {
      this.errors.remove('global');
      this.$validator.validateAll().then(validationWasSuccessful => {
        if (!validationWasSuccessful) {
          console.log('validation failed');
          return;
        }

        const variables = {
          input: {
            page: this.pagination.page,
            limit: this.pagination.limit,
            sort: {
              direction: this.sprayLogsTable.sortDesc
                ? 'DESC'
                : 'ASC',
              field: this.sprayLogsTable.sortBy,
            },
          },
        };

        if (this.hasDateRange) {
          variables.input.sprayed_at_gte = this.startDate;
          variables.input.sprayed_at_lte = this.endDate;
        }

        this.$apollo
          .mutate({
            mutation: exportSprayLogMutation,
            variables,

          })
          .then(({ data: { exportSprayLog: { url } } }) => {
            window.location.href = url;
            this.$notifySuccess('Spray log Export created!');
          })
          .catch(err => {
            this.$mapGraphQLError(err);
          })
          .catch(err => {
            console.error(err);
            this.$notifyFailure(
              'Unfortunately, we were unable to do that!',
            );
          });
      });
    },
    attemptDelete(log) {
      console.log(log);
      const notification = this.$notifyConfirm('Deleting a spray diary entry cannot be undone. Are you sure you wish to delete this?', {
        buttons: [
          this.$notification.button('Yes', 'btn btn-primary', () => {
            notification.close();
            this.doDelete(log);
          }),
          this.$notification.button('Cancel', 'btn btn-light', () => notification.close()),
        ],
      });
    },
    doDelete(log) {
      this.$apollo
        .mutate({
          mutation: deleteSprayLogMutation,
          variables: { input: { id: log.id, }, },
        })
        .then(() => {
          this.$notifySuccess('Spray Diary deleted successfully!');
          this.$apollo.queries.sprayLogs.refetch();
        })
        .catch(err => {
          console.error(err);
          this.$notifyFailure('Unfortunately, we were unable to do that!');
        });
    },
  },
};
</script>
